import type { FC } from "react";
import {
  HiLocationMarker,
  HiOutlineCalendar,
  HiOutlineClock,
  HiOutlineClipboardCheck,
} from "react-icons/hi";
import { useTranslation } from "react-i18next";
import {
  statusCancelled,
  statusCompleted,
  statusDraft,
  statusInProgress,
  statusNew,
  statusScheduled,
} from "@design-system";
import { IMapRoute } from "../types";
import { Driver } from "../../../../models";
import { OrderStatus } from "../../../../types/order/IOrder";
import moment from "moment";

interface OrderCardProps {
  order: IMapRoute;
  onClick?: () => void;
  isSelected?: boolean;
}

export const OrderCard: FC<OrderCardProps> = ({
  order,
  onClick,
  isSelected,
}) => {
  const { t } = useTranslation(["planner", "stats", "orders", "common", "map"]);

  // Format the first pickup and last delivery times/locations for display
  const firstStop = order.route?.[0];
  const lastStop = order.route?.[order.route.length - 1];

  // Format time for display
  const formatTime = (timestamp: any) => {
    if (!timestamp) return "";

    try {
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        return "";
      }

      return moment(date).format("HH:mm");
    } catch (error) {
      return "";
    }
  };

  // Format date for display
  const formatDate = (timestamp: any) => {
    if (!timestamp) return "";

    try {
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        return "";
      }

      return moment(date).format("DD/MM/YYYY");
    } catch (error) {
      return "";
    }
  };

  // Get status color and text
  const getStatusInfo = () => {
    switch (order.status) {
      case OrderStatus.Draft:
        return {
          color: statusDraft,
          text: t("stats:orderStatus.draft"),
        };
      case OrderStatus.New:
        return {
          color: statusNew,
          text: t("stats:orderStatus.new"),
        };
      case OrderStatus.Scheduled:
        return {
          color: statusScheduled,
          text: t("stats:orderStatus.scheduled"),
        };
      case OrderStatus.InProgress:
        return {
          color: statusInProgress,
          text: t("stats:orderStatus.in_progress"),
        };
      case OrderStatus.Completed:
        return {
          color: statusCompleted,
          text: t("stats:orderStatus.completed"),
        };
      case OrderStatus.Cancelled:
        return {
          color: statusCancelled,
          text: t("stats:orderStatus.cancelled"),
        };
      default:
        return {
          color: statusDraft,
          text: order.status || "",
        };
    }
  };

  // Calculate progress percentage for the route
  const getRouteProgress = () => {
    if (!order.route?.length) return 0;

    switch (order.status) {
      case OrderStatus.New:
        return 0;
      case OrderStatus.InProgress:
        return 33;
      case OrderStatus.Completed:
        return 100;
      case OrderStatus.Cancelled:
        return 0;
      default:
        return 0;
    }
  };

  const progressPercentage = getRouteProgress();
  const statusInfo = getStatusInfo();

  // Get display order identifier
  const getOrderDisplayText = () => {
    // First priority: Use orderNumber if available
    if (order.orderNumber) return order.orderNumber;

    // Second priority: Format ID if it includes dashes
    if (order.id?.includes("-")) return order.id;

    // Last priority: Create a formatted ID from the available information
    const type = order.type || "OR";
    const shortId = order.id?.substring(0, 5) || "00000";

    // Get first location's abbreviation if available
    let locationSuffix = "NA";
    if (firstStop?.location?.city) {
      // Take first 2 letters of the city
      locationSuffix = firstStop.location.city.substring(0, 2).toUpperCase();
    }

    return `${type}-${shortId}-${locationSuffix}`;
  };

  const orderDisplayText = getOrderDisplayText();

  // Compact card for non-selected items
  if (!isSelected) {
    return (
      <div
        onClick={onClick}
        className="p-3 transition-colors duration-200 cursor-pointer border rounded-lg border-gray-200 hover:border-purple-500 hover:bg-purple-50/30 mb-3 mx-2"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <div className="text-text">
              <HiOutlineClipboardCheck className="w-4 h-4 " />
            </div>
            <span className="text-text text-sm font-medium">
              {orderDisplayText}
            </span>
          </div>
          <div
            className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusInfo.color}`}
          >
            {statusInfo.text}
          </div>
        </div>

        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center text-muted">
            <HiLocationMarker className="h-3 w-3 mr-1" />
            <span>{firstStop?.location?.city || t("common:unknown")}</span>
            <span className="mx-1">→</span>
            <span>{lastStop?.location?.city || t("common:unknown")}</span>
          </div>

          <div className="flex items-center text-muted">
            <HiOutlineCalendar className="h-3 w-3 mr-1" />
            <span>{formatDate(firstStop?.stopDate)}</span>
          </div>
        </div>

        {order.driver && (
          <div className="flex items-center text-xs text-gray-500 mt-1">
            <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium mr-1">
              {order.driver.firstName?.[0] || "D"}
            </div>
            <span className="text-xs dark:text-gray-300">
              {Driver.getDriverName(order.driver)}
            </span>
          </div>
        )}
      </div>
    );
  }

  // Expanded card for selected items
  return (
    <div
      onClick={onClick}
      className="p-4 transition-colors duration-200 cursor-pointer border rounded-xl mb-3 mx-2 border-purple-500 bg-purple-50"
    >
      {/* Header: ID and Status */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <div className="text-gray-800">
            <HiOutlineClipboardCheck className="w-4 h-4" />
          </div>
          <span className="text-gray-800 text-sm font-medium">
            {orderDisplayText}
          </span>
        </div>
        <div
          className={`px-3 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}
        >
          {statusInfo.text}
        </div>
      </div>

      {/* Customer info */}
      <div className="flex items-center mb-3">
        <div className="text-xs text-gray-700">
          <span className="font-medium">{t("common:customer")}:</span>{" "}
          {order.customerName}
        </div>
      </div>

      {/* Route progress */}
      <div className="mt-2 mb-2">
        <div className="flex items-center mb-1">
          {/* Start point */}
          <div className="flex-none relative">
            <div
              className={`w-5 h-5 rounded-full ${order.status === OrderStatus.Completed ? "bg-green-500" : "bg-purple-600"} flex items-center justify-center`}
            >
              <div className="w-2 h-2 rounded-full bg-white"></div>
            </div>
            <div
              className={`absolute w-[calc(100%-5px)] h-[2px] ${order.status === OrderStatus.Completed ? "bg-green-500" : "bg-gray-200"} left-5 top-1/2 transform -translate-y-1/2`}
            ></div>
          </div>

          {/* Progress line */}
          <div
            className={`flex-grow h-[2px] ${order.status === OrderStatus.Completed ? "bg-green-500" : "bg-gray-200"} relative`}
          >
            {/* Progress indicator */}
            <div
              className={`absolute left-0 top-0 h-[2px] ${order.status === OrderStatus.Completed ? "bg-green-500" : "bg-purple-600"}`}
              style={{ width: `${progressPercentage}%` }}
            ></div>

            {/* Current position indicator */}
            {progressPercentage > 0 && progressPercentage < 100 && (
              <div
                className={`absolute w-4 h-4 rounded-full ${order.status === OrderStatus.Completed ? "bg-green-500" : "bg-purple-600"} -mt-[7px] flex items-center justify-center`}
                style={{
                  left: `${progressPercentage}%`,
                  transform: "translateX(-50%)",
                }}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
              </div>
            )}
          </div>

          {/* End point */}
          <div className="flex-none relative">
            <div
              className={`absolute w-[calc(100%-5px)] h-[2px] ${order.status === OrderStatus.Completed ? "bg-green-500" : "bg-gray-200"} right-5 top-1/2 transform -translate-y-1/2`}
            ></div>
            <div
              className={`w-5 h-5 rounded-full ${order.status === OrderStatus.Completed ? "bg-green-500" : "bg-gray-300"} flex items-center justify-center`}
            >
              <div className="w-2 h-2 rounded-full bg-white"></div>
            </div>
          </div>
        </div>

        {/* Start and end locations */}
        <div className="flex justify-between text-xs">
          <div>
            <div className="font-medium text-gray-800">
              {firstStop?.location?.city || t("map:origin")}
            </div>
            <div className="text-gray-500">
              {formatDate(firstStop?.stopDate)}
            </div>
          </div>
          <div className="text-right">
            <div className="font-medium text-gray-800">
              {lastStop?.location?.city || t("map:destination")}
            </div>
            <div className="text-gray-500">
              {formatDate(lastStop?.stopDate)}
            </div>
          </div>
        </div>
      </div>

      {/* Time and details */}
      <div className="flex items-center justify-between mt-3 text-xs text-gray-600">
        <div className="flex items-center">
          <HiOutlineClock className="h-3.5 w-3.5 mr-1 text-gray-500" />
          <span>{formatTime(firstStop?.stopDate)}</span>
        </div>
        <div className="flex items-center">
          <HiLocationMarker className="h-3.5 w-3.5 mr-1 text-gray-500" />
          <span className="truncate max-w-[180px]">
            {firstStop?.location?.addressLine ||
              firstStop?.location?.displayName}
          </span>
        </div>
      </div>

      {/* Driver info and actions */}
      {order.driver && (
        <div className="flex items-center mt-4 pt-3 border-t border-gray-100">
          <div className="flex items-center">
            <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium mr-2">
              {order.driver.firstName?.[0] ||
                order.driver.lastName?.[0] ||
                t("common:driver_initial")}
            </div>
            <div>
              <div className="text-xs font-medium">
                {Driver.getDriverName(order.driver)}
              </div>
              <div className="text-xs text-gray-500">{t("common:driver")}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
